
import React, {useEffect} from 'react';
import {useParams } from 'react-router-dom';
import {useGetBasicStorefrontQuery, useStorefrontAnonymousLoginMutation, useStorefrontLoginMutation} from '../Api/Api';
import {isDarkColor} from '../Components/Helpers/ColorHelpers';
import {useLocalizationFor} from '../Components/Helpers/useLocalization';
import StorefrontSignIn from '../Components/Auth/StorefrontSignIn';
import LoginPageShell, { useLoginPageStyles } from "../Components/Auth/LoginPageShell";
import {Button, Spinner} from "@fluentui/react-components";
import { useHostTenancyRedirect } from '../Components/Helpers/useHostTenancyRedirect';

const StorefrontLoginPage: React.FC = () => {
    const { organization, storefront } = useParams<{ organization: string, storefront: string }>();
    // On a custom domain, a deep link to another org's login bounces to this domain's own storefront.
    useHostTenancyRedirect(organization);
    const message = new URLSearchParams(window.location.search).get('message');
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    const sSettings = useGetBasicStorefrontQuery({ organization: organization ?? '', storefront: storefront ?? '' });
    const L = useLocalizationFor(sSettings.data?.localizationConfig);
    const loginStyles = useLoginPageStyles(sSettings.data?.loginPageConfig);
    const [nLogin] = useStorefrontAnonymousLoginMutation();
    const [loggingIn, setLoggingIn] = React.useState<boolean>(false);

    useEffect(() => {
        const data = sSettings.data;
        if (!data) return;
        let link: HTMLLinkElement | null = null;
        if (data.customCSS) {
            link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = data.customCSS;
        } else if (data.backgroundColor) {
            link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = isDarkColor(data.backgroundColor) ? '/themes/custom_dark.css' : '/themes/custom_light.css';
        }
        if (link) {
            document.head.appendChild(link);
            return () => { document.head.removeChild(link!); };
        }
    }, [sSettings.data]);
    
    // The email comes from StorefrontSignIn, which owns the input since the sign-in flow was shared
    // with the header popover. Reading it from local state here silently stopped pre-filling sign-up.
    const onSignup = (emailAddress?: string) => {
        if (emailAddress) {
            window.location.href = `/${organization}/${storefront}/signup?email=${encodeURIComponent(emailAddress)}`;
        }
        else{
            window.location.href = `/${organization}/${storefront}/signup`
        }
    }
    
    const onBrowseAsGuest = React.useCallback(() => {
        if(organization && storefront) {
            setLoggingIn(true)
            nLogin({organization, storefront}).then(a => {
                if (a.data?.redirectUrl) {
                    setLoggingIn(false)
                    window.location.assign(a.data?.redirectUrl);
                } else {
                    // nLogin refused (anonymous access revoked between the settings read and the
                    // call). Fall back to the form rather than leaving a dead spinner.
                    setLoggingIn(false);
                    setAutoGuestFailed(true);
                }
            })
        }
    }, [organization, storefront, nLogin])

    // loginMode 'guest': skip the form entirely and put the shopper straight on the home page as a
    // guest. Deliberately NOT applied when:
    //   * `message` is set — that's an expired/failed session being bounced here, and silently
    //     downgrading someone who HAS an account to a guest would strand them out of their orders.
    //   * `?login=1` — the explicit escape hatch, so the form stays reachable (the header's Sign in
    //     control and any "sign in instead" link point here).
    // The redirect the shopper was originally heading for is preserved by nLogin's own redirect.
    const [autoGuestFailed, setAutoGuestFailed] = React.useState(false);
    const forceForm = new URLSearchParams(window.location.search).get('login') === '1';
    const autoGuest = sSettings.data?.loginMode === 'guest' && !message && !forceForm && !autoGuestFailed;
    const autoGuestStarted = React.useRef(false);
    useEffect(() => {
        // Ref-guarded: this must fire exactly once, or a re-render mid-request mints a second session.
        if (!autoGuest || autoGuestStarted.current) return;
        autoGuestStarted.current = true;
        onBrowseAsGuest();
    }, [autoGuest, onBrowseAsGuest]);


    return (
        <LoginPageShell
            config={sSettings.data?.loginPageConfig}
            storefrontName={sSettings.data?.name}
            backgroundColor={sSettings.data?.backgroundColor}
            textColor={sSettings.data?.textColor}
            favicon={sSettings.data?.favicon}
            showIntro
        >
            <>
                {sSettings.isLoading && <Spinner label={L('localization_common_loading')} />}
                {sSettings.error && <>{L('localization_auth_error_retry')}</>}
                {sSettings.data && !loggingIn && !autoGuest && <>
                    {/* The sign-in state machine is shared with the header popover so the two surfaces
                        offer the same methods and cannot drift. Browse-as-Guest stays here: it is a way
                        of NOT signing in, so it belongs to the page rather than the sign-in flow. */}
                    <StorefrontSignIn
                        organization={organization ?? ''}
                        storefront={storefront ?? ''}
                        method={sSettings.data.loginMethod}
                        redirect={redirect ?? ''}
                        message={message ?? undefined}
                        allowSignup={!!sSettings.data.allowSignup}
                        onSignup={onSignup}
                        onSignedIn={(token: string) => {
                            const to = redirect ? `&redirect=${encodeURIComponent(redirect)}` : '';
                            window.location.assign(`/token?token=${encodeURIComponent(token)}${to}`);
                        }}
                        L={L}
                        inputStyle={loginStyles.input}
                        inputTextStyle={loginStyles.inputText}
                        primaryButtonStyle={loginStyles.primaryButton}
                        secondaryButtonStyle={loginStyles.secondaryButton}
                        messageStyle={loginStyles.message}
                    />
                    {sSettings.data.allowAnonymousAccess &&
                        <Button className='login-btn' appearance='subtle' style={{width: '100%', marginTop: '1em', ...loginStyles.secondaryButton}} onClick={() => onBrowseAsGuest()}>{L('localization_auth_browse_as_guest')}</Button>
                    }
                </>}
                {/* `autoGuest` covers the gap between the settings arriving and the nLogin request
                    starting, so the form never flashes on a guest-landing storefront. */}
                {(loggingIn || autoGuest) && <Spinner label={L('localization_auth_logging_in')} />}
            </>
        </LoginPageShell>
    );
}

export default StorefrontLoginPage;